import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CalendarIntervalDay } from 'platform-components';

import {
    COURSE_CALENDAR_COLUMN_INITIAL_STATE,
    COURSE_CALENDAR_EDITOR_INITIAL_STATE,
    VISIBLE_CARDS_CHUNK,
} from './constants';
import {
    CalendarClessons,
    CalendarCourse,
    CourseCalendarColumnsCard,
    CourseCalendarStore,
    MarkedMonthDays,
} from './typings';

const initialState: CourseCalendarStore = {};

const fillEmptyEditor = (state: Draft<CourseCalendarStore>, id: string): CourseCalendarStore => {
    if (state[id]) {
        return state;
    }

    state[id] = { ...COURSE_CALENDAR_EDITOR_INITIAL_STATE };

    return state;
};

const updateIntervalLoadingStatus = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{ editorId: string; status: LOADING_STATUSES }>
) => {
    const { editorId, status } = action.payload;

    fillEmptyEditor(state, editorId);

    state[editorId].intervalLoadingStatus = status;
};

const updateColumnLoadingStatus = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{ editorId: string; day: CalendarIntervalDay; status: LOADING_STATUSES }>
) => {
    const { editorId, status, day } = action.payload;

    fillEmptyEditor(state, editorId);

    const editor = state[editorId];

    if (editor.columns[day.date]) {
        editor.columns[day.date].columnLoadingStatus = status;
    }
};

const updateMarkedMonthDaysLoadingStatus = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{ editorId: string; status: LOADING_STATUSES }>
) => {
    const { editorId, status } = action.payload;

    fillEmptyEditor(state, editorId);

    state[editorId].markedMonthDaysLoadingStatus = status;
};

const updatePeriod = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{
        editorId: string;
        interval: CalendarIntervalDay[];
    }>
) => {
    const { editorId, interval } = action.payload;

    fillEmptyEditor(state, editorId);

    const editor = state[editorId];

    editor.currentInterval = interval;
    editor.clessons = {};
    editor.columns = {};
};

const updateColumnVisibleCards = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{
        editorId: string;
        day: CalendarIntervalDay;
        maxVisibleCards: number;
    }>
) => {
    const { editorId, day, maxVisibleCards } = action.payload;

    fillEmptyEditor(state, editorId);

    const editor = state[editorId];

    editor.columns[day.date].maxVisibleCards = maxVisibleCards;
};

const updateClessonCards = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{
        editorId: string;
        cardsByDate: Record<string, CourseCalendarColumnsCard[]>;
        clessons: CalendarClessons;
        isAllCardsLoaded: boolean;
    }>
) => {
    const { editorId, cardsByDate, clessons, isAllCardsLoaded } = action.payload;

    fillEmptyEditor(state, editorId);

    const editor = state[editorId];

    editor.clessons = {
        ...editor.clessons,
        ...clessons,
    };

    editor.currentInterval.forEach(({ date }) => {
        if (cardsByDate[date]) {
            const isAllLoaded = isAllCardsLoaded || cardsByDate[date].length < VISIBLE_CARDS_CHUNK;

            if (editor.columns[date]) {
                editor.columns[date].isAllCardsLoaded = isAllCardsLoaded;
                editor.columns[date].columnLoadingStatus = LOADING_STATUSES.SUCCESS;
                editor.columns[date].cards = isAllLoaded
                    ? cardsByDate[date]
                    : [...editor.columns[date].cards, ...cardsByDate[date]];
            } else {
                editor.columns[date] = {
                    ...COURSE_CALENDAR_COLUMN_INITIAL_STATE,
                    cards: cardsByDate[date],
                    isAllCardsLoaded,
                };
            }
        }
    });
};

const fetchMonthLessonsSuccess = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{
        editorId: string;
        markedMonthDays: MarkedMonthDays;
    }>
) => {
    const { editorId, markedMonthDays } = action.payload;

    fillEmptyEditor(state, editorId);

    const editor = state[editorId];

    editor.markedMonthDays = {
        ...editor.markedMonthDays,
        ...markedMonthDays,
    };
};

const resetEditor = (
    state: Draft<CourseCalendarStore>,
    action: PayloadAction<{
        editorId: string;
    }>
) => {
    const { editorId } = action.payload;
    state[editorId] = { ...COURSE_CALENDAR_EDITOR_INITIAL_STATE };
};

const slice = createSlice({
    name: 'COURSE_CALENDAR',
    initialState: initialState,
    reducers: {
        updateIntervalLoadingStatus,
        updateMarkedMonthDaysLoadingStatus,
        updateColumnLoadingStatus,
        updatePeriod,
        updateClessonCards,
        updateColumnVisibleCards,
        fetchMonthLessonsSuccess,
        resetEditor,
        fetchPeriodLessons: (
            state: Draft<CourseCalendarStore>,
            // eslint-disable-next-line
            action: PayloadAction<{
                editorId: string;
                interval: CalendarIntervalDay[];
                courses: CalendarCourse[];
                classId: string;
                subject: string;
                limit?: number;
            }>
        ) => state,
        fetchDayLessons: (
            state: Draft<CourseCalendarStore>,
            // eslint-disable-next-line
            action: PayloadAction<{
                editorId: string;
                interval: CalendarIntervalDay[];
                courses: CalendarCourse[];
                classId: string;
                subject: string;
                limit?: number;
            }>
        ) => state,
        fetchMonthLessons: (
            state: Draft<CourseCalendarStore>,
            // eslint-disable-next-line
            action: PayloadAction<{ editorId: string; interval: CalendarIntervalDay[]; courses: CalendarCourse[] }>
        ) => state,
    },
});

export const actions = slice.actions;
export const reducer = slice.reducer;
